import {useQuery} from '@tanstack/react-query';
import {Alert, Box, CircularProgress, Typography} from '@mui/material';
import NumericPair from '../../components/NumericPair';
import Section from '../../components/Section';
import {fetchSlugs} from '../utils/helpers';
import {TOOLTIPS, buildIniziativaSlugs} from '../utils/slugs';
import {DashboardData} from '../utils/types';
import {useLuoghi2025Auth} from '../utils/useLuoghi2025Auth';

const SURVEY_PARAMS = ['survey4_id'];

const BOX_LABELS = {uoa: 'La mia iniziativa', average: 'Media delle iniziative'};

const METHOD_NOTE =
    'Nota metodologica: i valori mancanti non sono assimilati a zero. Nei casi in cui il denominatore sia pari a zero, l’indicatore è restituito come non calcolabile.';

interface DashboardProps {
    container: string;
    uoaId: string;
    fruitoriSurveyId: number;
}

const Dashboard = ({container, uoaId, fruitoriSurveyId}: DashboardProps) => {
    const {data, isLoading, isError} = useQuery<DashboardData>({
        queryKey: ['luoghi-2025-iniziativa', container, uoaId, fruitoriSurveyId],
        queryFn: () => fetchSlugs(container, uoaId, buildIniziativaSlugs(fruitoriSurveyId)),
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    return (
        <Box>
            <Typography variant='caption' color='text.disabled' sx={{display: 'block', mb: 2}}>
                {METHOD_NOTE}
            </Typography>

            <Section title='I. SODDISFAZIONE DEI FRUITORI PER LE INIZIATIVE'>
                <NumericPair
                    title='Soddisfazione dei fruitori a confronto con la media'
                    tooltip={TOOLTIPS.soddisfazione}
                    uoaValue={data.soddisfazione.uoa}
                    averageValue={data.soddisfazione.average}
                    isCount
                    labels={BOX_LABELS}
                />
            </Section>
        </Box>
    );
};

// Dashboard 2 of the prototype — pagina "Luoghi da rigenerare e iniziative", livello Iniziativa.
const Iniziativa2025 = () => {
    const {container, uoaId, surveyIds, isAuthorized} = useLuoghi2025Auth(SURVEY_PARAMS);

    if (!isAuthorized) {
        return <Alert severity='error'>Parametri mancanti o chiave non valida.</Alert>;
    }

    return (
        <Dashboard
            container={container!}
            uoaId={uoaId!}
            fruitoriSurveyId={Number(surveyIds['survey4_id'])}
        />
    );
};

export default Iniziativa2025;
